import {defineComponent, h, mergeProps} from 'vue';
import type {PropType} from 'vue';
import type {IconProps, IconSet} from 'vuetify';
import {mdiIconMap} from './mdi-icons';

// Custom Vuetify icon set: resolves the `mdi-*` names already used across the
// app (via client-core `Icons`, raw strings and Vuetify aliases) to tree-shaken
// SVG paths from @mdi/js — no `@mdi/font` webfont download (ticket T00HNMV7BCIH).
// Registered under the name `mdi`, so every existing call site keeps working
// unchanged.
//
// Built like Vuetify's own VSvgIcon on purpose: a declared `props` option plus
// `inheritAttrs: false` and an explicit `mergeProps(attrs, …)`. A functional
// component without a declared `props` option gets NO automatic attribute
// fallthrough — everything a call site puts on `<v-icon>` beyond tag/icon
// (an `id`, an `aria-label`, the `v-bind` activator props of v-menu/v-tooltip)
// would be silently dropped. That is how `id="menuNotifications"` on the bell
// went missing (T00P69U5AFOQ).
const MdiSvgIcon = defineComponent({
    name: 'MdiSvgIcon',
    inheritAttrs: false,
    props: {
        icon: {
            type: [String, Array, Object, Function] as PropType<IconProps['icon']>,
            default: '',
        },
        tag: {
            type: [String, Object, Function] as PropType<IconProps['tag']>,
            required: true,
        },
        disabled: Boolean,
    },
    setup(props, {attrs}) {
        return () => {
            const iconName = typeof props.icon === 'string' ? props.icon : '';
            // The `mdi-*` name stays on the root element as a class, exactly like the
            // webfont set did. No stylesheet depends on it, but it is the stable hook
            // the Playwright e2e suite uses to address icons (`i.mdi-dots-vertical`).
            return h(
                props.tag,
                mergeProps(attrs, {class: iconName}),
                h(
                    'svg',
                    {
                        class: 'v-icon__svg',
                        xmlns: 'http://www.w3.org/2000/svg',
                        viewBox: '0 0 24 24',
                        role: 'img',
                        'aria-hidden': 'true',
                    },
                    [h('path', {d: mdiIconMap[iconName] ?? ''})]
                )
            );
        };
    },
});

export const mdiSvgIconSet: IconSet = {
    component: MdiSvgIcon,
};
